import { useState, useEffect, useRef } from 'react';
import { useDebouncedValue } from '@/hooks/use-debounce';
import { Input } from '@/components/ui/input';
import { Loader2, MapPin } from 'lucide-react';
import { cn } from '@/lib/utils';
import { env } from '@/lib/env';

export interface AddressSuggestion {
  display_name: string;
  place_id: string;
  formatted_address: string;
  address_components?: {
    long_name: string;
    short_name: string;
    types: string[];
  };
  geometry?: {
    location: {
      lat: number;
      lng: number;
    };
  };
}

interface AddressAutocompleteProps {
  value: string;
  onValueChange: (value: string) => void;
  onSelect?: (address: string, details?: AddressSuggestion) => void;
  onBlur?: () => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
}

interface NominatimResult {
  display_name: string;
  place_id: number;
  addresstype?: string;
  class?: string;
  address?: {
    city?: string;
    town?: string;
    village?: string;
    hamlet?: string;
    state?: string;
    country?: string;
  };
}

interface GeoapifySuggestion {
  formatted: string;
  properties: {
    place_id: string;
    name: string;
    housenumber?: string;
    street?: string;
    city?: string;
    postcode?: string;
    country?: string;
    country_code?: string;
    state?: string;
    county?: string;
    suburb?: string;
    district?: string;
    result_type?: string;
    lat: number;
    lon: number;
  };
}

// Re-rank Geoapify predictions so that streets and cities outrank regions and
// generic POIs. Geoapify often returns the Oblast (state) or unrelated entities
// like rivers (`amenity` / `unknown`) before the actual city for many Ukrainian
// cities — see HW-390. Lower number = higher priority. Stable sort preserves
// relevance order within a tier.
const RESULT_TYPE_PRIORITY: Record<string, number> = {
  street: 1,
  building: 1,
  postcode: 1,
  city: 2,
  suburb: 2,
  district: 2,
  county: 3,
  state: 3,
  amenity: 4,
  country: 5,
  unknown: 5,
};

function getResultTypePriority(props: GeoapifySuggestion['properties']): number {
  const explicit = props.result_type;
  if (explicit && explicit in RESULT_TYPE_PRIORITY) return RESULT_TYPE_PRIORITY[explicit];
  // Fallback inference when result_type is missing or unrecognized.
  if (props.street) return RESULT_TYPE_PRIORITY.street;
  if (props.city) return RESULT_TYPE_PRIORITY.city;
  if (props.state) return RESULT_TYPE_PRIORITY.state;
  return RESULT_TYPE_PRIORITY.unknown;
}

// Same idea for Nominatim (the no-API-key fallback): demote oblasts and
// natural features (rivers, etc.) below cities and streets. Nominatim sorts by
// `importance`, which for Ukrainian queries puts "Poltava Oblast" above
// "Poltava" and "Dnieper River" above the city of "Dnipro" — see HW-390.
function getNominatimPriority(item: NominatimResult): number {
  const at = item.addresstype ?? '';
  const cls = item.class ?? '';
  if (at === 'road' || at === 'building' || at === 'house' || at === 'postcode') return 1;
  if (
    at === 'city' ||
    at === 'town' ||
    at === 'village' ||
    at === 'hamlet' ||
    at === 'suburb' ||
    at === 'district' ||
    at === 'neighbourhood'
  ) {
    return 2;
  }
  if (at === 'county' || at === 'state' || at === 'region' || at === 'province') return 3;
  if (
    cls === 'waterway' ||
    cls === 'natural' ||
    cls === 'amenity' ||
    cls === 'leisure' ||
    cls === 'tourism'
  )
    return 4;
  if (at === 'country') return 5;
  return 5;
}

export function AddressAutocomplete({
  value,
  onValueChange,
  onSelect,
  onBlur,
  placeholder = 'Search address...',
  disabled = false,
  className,
  id,
}: AddressAutocompleteProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState(value);
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const debouncedQuery = useDebouncedValue(searchQuery, 300);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const isUserTypingRef = useRef(false);
  const lastValueRef = useRef(value);

  // Sync searchQuery with value prop only if value changed externally
  // Don't sync while user is actively typing
  useEffect(() => {
    // Only sync if value changed externally and user is not typing
    if (value !== lastValueRef.current && !isUserTypingRef.current) {
      setSearchQuery(value);
    }
    lastValueRef.current = value;
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [open]);

  // Fetch address suggestions using Geoapify API
  useEffect(() => {
    if (!debouncedQuery || debouncedQuery.trim().length < 2) {
      setSuggestions([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const controller = new AbortController();
    const query = debouncedQuery.trim();

    // Use Geoapify Geocoding API (free tier: 3000 requests/day)
    const apiKey = env.VITE_GEOAPIFY_API_KEY;

    if (!apiKey) {
      // Fallback to Nominatim if no API key
      fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=10&addressdetails=1&accept-language=en`,
        {
          signal: controller.signal,
          headers: {
            'User-Agent': 'HyggeOS',
            'Accept-Language': 'en',
          },
        }
      )
        .then((res) => {
          if (!res.ok) {
            throw new Error(`HTTP error! status: ${res.status}`);
          }
          return res.json();
        })
        .then((data: unknown) => {
          if (Array.isArray(data)) {
            const ranked = (data as NominatimResult[])
              .filter((item) => item.class !== 'waterway' && item.class !== 'natural')
              .sort((a, b) => getNominatimPriority(a) - getNominatimPriority(b));
            const formattedSuggestions: AddressSuggestion[] = ranked.map((item) => {
              // Build clean display: "City, State, Country" — skip Hromada/Raion noise
              const addr = item.address || {};
              const place = addr.city || addr.town || addr.village || addr.hamlet || '';
              const parts = [place, addr.state, addr.country].filter(Boolean);
              const displayName = parts.length > 1 ? parts.join(', ') : item.display_name || '';

              return {
                display_name: displayName,
                place_id: item.place_id?.toString() || Math.random().toString(),
                formatted_address: displayName,
              };
            });
            setSuggestions(formattedSuggestions);
          } else {
            setSuggestions([]);
          }
          setIsLoading(false);
        })
        .catch((err: unknown) => {
          if (err instanceof Error && err.name !== 'AbortError') {
            // eslint-disable-next-line no-console
            console.error('Address autocomplete error:', err);
            setSuggestions([]);
            setIsLoading(false);
          }
        });
      return;
    }

    // Use Geoapify API
    fetch(
      `https://api.geoapify.com/v1/geocode/autocomplete?text=${encodeURIComponent(query)}&limit=10&lang=en&apiKey=${apiKey}`,
      {
        signal: controller.signal,
        headers: {
          Accept: 'application/json',
        },
      }
    )
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        return res.json();
      })
      .then((data: { features?: GeoapifySuggestion[] }) => {
        if (data.features && Array.isArray(data.features)) {
          // Re-rank by result_type so cities/streets beat regions and POIs.
          const ranked = [...data.features].sort(
            (a, b) => getResultTypePriority(a.properties) - getResultTypePriority(b.properties)
          );
          const formattedSuggestions: AddressSuggestion[] = ranked.map((feature) => {
            const props = feature.properties;

            // Format: COUNTRY, region, city, street (if present)
            const addressParts: string[] = [];

            // Country first
            if (props.country) {
              addressParts.push(props.country);
            }

            // Region/State
            if (props.state) {
              addressParts.push(props.state);
            }

            // City - check multiple possible fields
            const city = props.city || props.suburb || props.district || props.county || '';
            if (city && !addressParts.includes(city)) {
              addressParts.push(city);
            }

            // Street (with house number if available)
            if (props.housenumber && props.street) {
              addressParts.push(`${props.street} ${props.housenumber}`);
            } else if (props.street) {
              addressParts.push(props.street);
            } else if (props.name && !city && !props.street) {
              // Use name if no street but also no city (to avoid duplication)
              addressParts.push(props.name);
            }

            // Drop duplicates — for state-only results props.name often equals
            // props.state ("Poltava Oblast, Ukraine, Poltava Oblast"); same can
            // happen with county === state.
            const uniqueParts = Array.from(new Set(addressParts));
            const formattedAddress = uniqueParts.join(', ');
            const fullAddress = props.postcode
              ? `${formattedAddress}, ${props.postcode}`
              : formattedAddress;

            // Check if Geoapify's formatted address is complete (not just country)
            const geoapifyFormatted =
              feature.formatted && feature.formatted.trim().length > 0
                ? feature.formatted.trim()
                : null;

            const isOnlyCountry =
              geoapifyFormatted && props.country && geoapifyFormatted === props.country;

            // Use our custom formatting if Geoapify only provides country
            // Otherwise prefer Geoapify's formatted address
            const finalAddress =
              geoapifyFormatted && !isOnlyCountry && addressParts.length <= 1
                ? geoapifyFormatted
                : fullAddress; // Use our formatted address with all parts

            return {
              display_name: finalAddress,
              place_id: props.place_id,
              formatted_address: finalAddress,
              address_components: {
                long_name: props.housenumber || props.street || props.name || '',
                short_name: '',
                types: [],
              },
              geometry: {
                location: {
                  lat: props.lat,
                  lng: props.lon,
                },
              },
            };
          });
          setSuggestions(formattedSuggestions);
        } else {
          setSuggestions([]);
        }
        setIsLoading(false);
      })
      .catch((err: unknown) => {
        if (err instanceof Error && err.name !== 'AbortError') {
          // eslint-disable-next-line no-console
          console.error('Address autocomplete error:', err);
          setSuggestions([]);
          setIsLoading(false);
        }
      });

    return () => {
      controller.abort();
    };
  }, [debouncedQuery]);

  const handleSelectSuggestion = (suggestion: AddressSuggestion) => {
    const fullAddress = suggestion.formatted_address || suggestion.display_name;

    // Update state immediately
    setSearchQuery(fullAddress);
    onValueChange(fullAddress);

    if (onSelect) {
      onSelect(fullAddress, suggestion);
    }

    // Close dropdown
    setOpen(false);

    // Ensure input has the correct value
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.value = fullAddress;
        setSearchQuery(fullAddress);
        inputRef.current.focus();
      }
    }, 50);
  };

  const handleInputBlur = () => {
    isUserTypingRef.current = false;
    onValueChange(searchQuery.trim());
    onBlur?.();
    setTimeout(() => {
      setOpen(false);
    }, 200);
  };

  return (
    <div ref={containerRef} className="relative w-full">
      <MapPin className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground pointer-events-none z-10" />
      <Input
        ref={inputRef}
        id={id}
        type="text"
        value={searchQuery}
        onChange={(e) => {
          const newValue = e.target.value;
          isUserTypingRef.current = true;
          setSearchQuery(newValue);

          if (newValue.trim().length >= 2) {
            setOpen(true);
          } else {
            setOpen(false);
          }

          setTimeout(() => {
            isUserTypingRef.current = false;
          }, 100);
        }}
        onBlur={handleInputBlur}
        onKeyDown={(e) => {
          // Only handle specific keys, let everything else work normally
          if (e.key === 'Enter') {
            e.preventDefault();
            if (searchQuery.trim().length > 0) {
              onValueChange(searchQuery.trim());
            }
            setOpen(false);
            inputRef.current?.blur();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            setOpen(false);
            inputRef.current?.blur();
          } else if (e.key === 'ArrowDown' && suggestions.length > 0 && open) {
            e.preventDefault();
            const firstSuggestion = dropdownRef.current?.querySelector(
              '[data-suggestion-item]'
            ) as HTMLElement;
            if (firstSuggestion) {
              firstSuggestion.focus();
            }
          }
          // For Space and all other keys - don't prevent default, let them work normally
        }}
        onFocus={() => {
          // Open dropdown on focus if there's enough text
          if (searchQuery.trim().length >= 2) {
            setOpen(true);
          }
        }}
        onClick={() => {
          // Open dropdown on click if there's enough text
          if (searchQuery.trim().length >= 2) {
            setOpen(true);
          }
        }}
        disabled={disabled}
        placeholder={placeholder}
        className={cn('pl-9 cursor-text', className)}
      />
      {open && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-popover text-popover-foreground rounded-md border shadow-md max-h-[300px] overflow-y-auto"
          style={{
            width: inputRef.current?.offsetWidth || '100%',
          }}
        >
          <div className="p-1">
            {isLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-4 w-4 animate-spin" />
              </div>
            ) : suggestions.length === 0 ? (
              <div className="px-2 py-6 text-center text-sm text-muted-foreground">
                {searchQuery && searchQuery.trim().length >= 2
                  ? 'No addresses found.'
                  : 'Type at least 2 characters to search...'}
              </div>
            ) : (
              <div className="space-y-1">
                {suggestions.map((suggestion) => {
                  const addressValue = suggestion.formatted_address || suggestion.display_name;
                  return (
                    <button
                      key={suggestion.place_id}
                      data-suggestion-item
                      type="button"
                      onClick={() => handleSelectSuggestion(suggestion)}
                      onMouseDown={(e) => {
                        // Prevent input blur when clicking
                        e.preventDefault();
                      }}
                      className="w-full flex items-start gap-2 px-2 py-1.5 text-sm rounded-sm hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none text-left"
                    >
                      <MapPin className="h-4 w-4 shrink-0 text-muted-foreground mt-0.5" />
                      <span className="truncate flex-1">{addressValue}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
